/*
  Who is allowed to own an initiative.

  Initiative ownership was briefly open to any account; it is now this fixed
  group. Matched on the EMAIL LOCAL-PART, case-insensitively, so it holds
  whether an address is @indorama.com or @ng.indorama.com and does not break
  when somebody's display name is edited — a name is not an identity.

  This is the ONE place the list lives. Both the Create Initiative dialog and
  the initiative detail page read it, so adding or removing someone is a
  one-line change here rather than a hunt through the pages.

  LIMIT WORTH KNOWING: this is a frontend rule. It decides what the owner picker
  offers, not what the database accepts — the anon key ships in the JS bundle,
  so a direct PATCH could still write any id into initiatives.owner_ids.
  Enforcing it server-side means either a trigger validating owner_ids against
  these addresses (hard-coding people in SQL, which drifts from this file) or a
  `can_own_initiative` flag on public.users that an admin maintains in the Team
  page. The flag is the right answer if this list is ever expected to change
  without a deploy.
*/

export const INITIATIVE_OWNER_HANDLES = [
  "sandeep.naruka",
  "deepu.sivadas",
  "sanjay.garg",
  "rahul.moondra",
  "munish.jindal",

  // "nitish.kopparaju" was here TEMPORARILY (added 2026-09-04) to test report
  // and digest emails end to end — removed 2026-09-16 now that testing is
  // done, per its own comment above. If it needs to go back in for another
  // testing round, it doubles as both the initiative-owner rule and the
  // digest/report "To" pool (see the comment below) — remove it again
  // afterward rather than leaving it.
];


/** The local-part of an email, lowercased. "A.B@x.com" -> "a.b". */
const handleOf = (email) => String(email || "").trim().toLowerCase().split("@")[0];

/*
  This same group is the only one allowed in the To field of a digest or report
  email (see components/RecipientFields.jsx): a report is addressed TO the
  owners and copies everyone else.
*/

/** Is this api.js-shaped user row allowed to own an initiative? */
export function isEligibleInitiativeOwner(user) {
  return INITIATIVE_OWNER_HANDLES.includes(handleOf(user?.email));
}

/**
 * The rows to offer in an owner picker.
 *
 * Eligible accounts, plus anyone ALREADY named as an owner even if they are not
 * eligible — an initiative that predates this rule must not have an owner
 * silently dropped the next time somebody saves it. Those rows are marked so
 * the reason they are listed is visible.
 */
export function initiativeOwnerOptions(users = [], selectedIds = []) {
  return users
    .filter((u) => isEligibleInitiativeOwner(u) || selectedIds.includes(u._id))
    .map((u) => (isEligibleInitiativeOwner(u) ? u : { ...u, role: "not eligible" }));
}
